import React, { FC, useEffect, useState } from 'react';

import { Divider } from 'antd';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { CodeAuthenticatorSnapshotProvider } from '@/components/Common/CodeAuthenticator';
import {
    useStores,
    useTargetScreen,
    useLoginMethodPreference,
    LOGIN_METHOD_EMAIL,
} from '@/components/hooks';
import {
    JUST_LOGGED_IN_TIMESTAMP_KEY,
    JUST_LOGGED_IN_TIMEOUT_MS,
} from '@/components/MiniApps/Common/constants';

import { FileAccessPopupsProvider } from './FileAccessPopupsContext';
import FilesActionsProvider from './FilesActionsContext';
import FilesRow from './FilesRow';
import OpenSecurely from './FilesRow/ActionsColumn/OpenSecurely';
import FilesCarousel from './FilesCarousel';
import Greeting from './Greeting';
import { MessageView } from './MessageView';
import {
    useVerifyAction,
    useOTPState,
    useOTPToggle,
    useDownloadActionError,
} from './hooks';
import { getContentType } from './helpers';
import styles from './LinkView.module.scss';
import OTPAuthSteps from '@/components/MiniApps/Common/OTPAuthSteps';
import {
    REQUIRED_PASSWORD_VERIFICATION,
} from '@/consts';
import { BASEURL, ENDPOINTS } from '@/api';
import { OpenOptions } from '@/config/openOptions.ts';
import { checkMessageContainsPermissions } from '@/components/utils';
import SkeletonView from '@/components/MiniApps/RecipientPage/InnerContent/SkeletonView';

const LinkView: FC = observer(() => {
    const [stepsInitialized, setStepsInitialized] = useState(false);
    const [mfaStatusLoaded, setMfaStatusLoaded] = useState(false);
    const isMobile = useTargetScreen('md');
    const { getLoginMethodPreference } = useLoginMethodPreference();

    const stepsMounted = (): void => {
        setStepsInitialized(true);
    };

    const {
        authSettingsStore: { API },
        recipientPageStore: { sharedFilesStore, emailMessageStore },
        filesAccessStore,
        userStore,
    } = useStores();

    const {
        authRequirements,
        accessibleFilesAllowForwarding,
        attachmentsCount,
        setIsContentLoading,
        isMainDataLoaded,
        isMFARequired,
        setIsMFARequired,
        accessibleFiles,
        isContentLoading,
        updateRequestsMap,
        anyRequestInProcess,
        permissionsLoaded,
        allPermissionsFail,
        setErrorType,
    } = sharedFilesStore;
    const { selectedAccessFileId } = filesAccessStore;
    const { messageId } = emailMessageStore;

    const {
        showOTP,
        closeOTP,
        openOTP,
    } = useOTPState();

    const {
        verifyAccess,
        postAuthAction,
        setAction,
        lastAccessType,
    } = useVerifyAction(sharedFilesStore, API);

    useOTPToggle({
        showOTP,
        closeOTP,
        openOTP,
        filesAccessStore,
        sharedFilesStore,
        userStore,
        lastAccessType,
        postAuthAction,
        messageId,
    });

    useDownloadActionError(filesAccessStore, sharedFilesStore, openOTP);

    const { phone: hasPhoneVerification, password: hasPasswordVerification } = authRequirements;
    const hasAllowForwarding = accessibleFilesAllowForwarding;

    const isMFAReqiredNeeded = (hasPhoneVerification || hasPasswordVerification) ? isMFARequired : false;

    const isAuthorized = userStore.isUserSetUp
        && mfaStatusLoaded
        && !isMFAReqiredNeeded
        && permissionsLoaded
        && !allPermissionsFail;

    const contentType = getContentType({
        showOTP,
        selectedFileId: selectedAccessFileId === messageId ? null : selectedAccessFileId,
        isAuthorized: !anyRequestInProcess && isAuthorized,
    });

    const showOpenViewerButton = contentType !== 'fileView';

    useEffect(() => {
        async function checkIsMFARequired(): Promise<void> {
            updateRequestsMap('checkIsMFARequired', true);
            try {
                const mfaFile = accessibleFiles.find((item) => item.policy.isMfa);
                // Also detect password-only policies: policy.isMfa is false for them,
                // but an openFile call will still return 401 if the password hasn't been entered.
                const passwordFile = accessibleFiles.find((item) => item?.policy?.settings?.access?.some(
                    (setting) => setting.id === REQUIRED_PASSWORD_VERIFICATION && setting.value === true,
                ));
                const fileToCheck = mfaFile || passwordFile;
                if (!fileToCheck) {
                    setIsMFARequired(false);
                    return;
                }

                const filesWithMfa = fileToCheck.file_id;
                const result = await API.post(
                    BASEURL.backend(),
                    ENDPOINTS.openFile(filesWithMfa),
                    { body: { app_name: OpenOptions.viewer } },
                );
                if (result) {
                    setIsMFARequired(false);
                }
            } catch (error) {
                const errorMessage = (error as any).response?.data?.message;
                const errorStatus = (error as any).response?.status;
                const mfaMessage = 'Mfa required';
                const permissionsMessage = 'User doesn\'t have permissions for this operation.';
                const passwordMessage = 'Missing protection password';

                // 406 means the viewer app is not allowed by the file's policy; it is not
                // an MFA signal. Treating it as MFA required would incorrectly block the page.
                if (errorStatus === 406) {
                    setIsMFARequired(false);
                    return;
                }

                const isExplicitMfaRequired = (
                    errorMessage === mfaMessage
                    || errorMessage === passwordMessage
                    || (hasAllowForwarding && errorMessage === permissionsMessage)
                );
                // For logged-in users, any openFile failure that is NOT a permissions error
                // means MFA is still required. This covers:
                //   • 403 adminAccessDenied (backend message that doesn't match mfaMessage)
                //   • CORS-blocked 403 responses (error.response is null → errorMessage is
                //     undefined), which happen on localhost when the backend omits CORS
                //     headers from error responses.
                const isUserLoggedIn = userStore.isUserSetUp;
                const isPermissionsError = !hasAllowForwarding && checkMessageContainsPermissions(errorMessage);
                if (isExplicitMfaRequired || (isUserLoggedIn && !isPermissionsError)) {
                    setIsMFARequired(true);
                }
            } finally {
                updateRequestsMap('checkIsMFARequired', false);
                setMfaStatusLoaded(true);
            }
        }

        if (permissionsLoaded) {
            if (hasPhoneVerification || hasPasswordVerification) {
                setMfaStatusLoaded(false);
                checkIsMFARequired();
            } else {
                setIsMFARequired(false);
                setMfaStatusLoaded(true);
            }
        }
    }, [
        API,
        accessibleFiles,
        hasPasswordVerification,
        hasPhoneVerification,
        permissionsLoaded,
        setIsMFARequired,
        updateRequestsMap,
    ]);

    useEffect(() => {
        if (!anyRequestInProcess && isMainDataLoaded && permissionsLoaded && mfaStatusLoaded) {
            const loginPreference = getLoginMethodPreference();
            const willRedirectToLogin = !userStore.isUserSetUp && loginPreference === LOGIN_METHOD_EMAIL;

            const justLoggedInTimestamp = sessionStorage.getItem(JUST_LOGGED_IN_TIMESTAMP_KEY);
            const hasJustLoggedIn = justLoggedInTimestamp
                && (Date.now() - parseInt(justLoggedInTimestamp, 10)) < JUST_LOGGED_IN_TIMEOUT_MS;
            const suppressedByJustLoggedIn = hasJustLoggedIn && !isMFAReqiredNeeded;
            if ((!userStore.isUserSetUp || isMFAReqiredNeeded) && !suppressedByJustLoggedIn && !willRedirectToLogin) {
                openOTP();
            } else {
                closeOTP();
                setIsContentLoading(false);
            }
        }
    }, [
        anyRequestInProcess,
        isMFAReqiredNeeded,
        isMainDataLoaded,
        mfaStatusLoaded,
        openOTP,
        permissionsLoaded,
        setIsContentLoading,
        userStore.isUserSetUp,
        getLoginMethodPreference
    ]);

    const allPermissionsError = allPermissionsFail && permissionsLoaded;

    useEffect(() => {
        if (allPermissionsError && !hasAllowForwarding) {
            setErrorType('noPermissions');
        }
    }, [allPermissionsError, hasAllowForwarding, setErrorType]);

    return (
        <FilesActionsProvider
            openOTP={openOTP}
            setAction={setAction}
        >
            <FileAccessPopupsProvider>
                <CodeAuthenticatorSnapshotProvider>
                    <div className={classNames(styles.container, { [styles['new-ui']]: contentType === 'OTP' })}>
                        {contentType === 'OTP' && (
                            <OTPAuthSteps
                                openFile={verifyAccess}
                                closeOTP={closeOTP}
                                stepsInitialized={stepsInitialized}
                                stepsMounted={stepsMounted}
                            />
                        )}
                        {contentType === 'fileView' && !anyRequestInProcess && <FilesCarousel />}
                        {contentType === 'message' && !isContentLoading && (
                            <div className={styles['main-content-wrapper']}>
                                <div className={styles['main-content-column']}>
                                    <Greeting />
                                    <Divider
                                        className={classNames({
                                            [styles.divider]: !isMobile,
                                            [styles['mobile-divider']]: isMobile,
                                        })}
                                    />
                                    {contentType === 'message' && <MessageView />}
                                </div>
                                {isMobile && !!attachmentsCount && <OpenSecurely />}
                            </div>
                        )}
                        {contentType !== 'OTP' && !isContentLoading && (
                            <FilesRow
                                showOpenViewerButton={showOpenViewerButton}
                                contentType={contentType}
                            />
                        )}
                        {contentType !== 'OTP' && isContentLoading && (
                            <SkeletonView />
                        )}
                    </div>
                </CodeAuthenticatorSnapshotProvider>
            </FileAccessPopupsProvider>
        </FilesActionsProvider>

    );
});

export default LinkView;
